import { Component, type ErrorInfo, type ReactNode } from 'react'
import styles from './ErrorBoundary.module.css'

/**
 * QUIL — Error boundary (Phase 3, A1/A2).
 *
 * The ONE class component in the codebase — React error boundaries have no hook
 * form (`getDerivedStateFromError`/`componentDidCatch` are class-only). Catches a
 * render throw (a page bug, or a lazy chunk that fails to load after a redeploy /
 * offline) so it shows a recoverable fallback instead of white-screening the app.
 *
 * `resetKeys` lets a parent recover the boundary when context changes (e.g. the
 * route/chapter id) without a full reload.
 */
interface Props {
  children: ReactNode
  /** Fallback UI. A function receives the error + a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode
  /** Reset the boundary when any of these values change (shallow-compared). */
  resetKeys?: unknown[]
  /** Side-effect on catch (e.g. attempt a save flush). */
  onError?: (error: Error, info: ErrorInfo) => void
  /** Short label for the default fallback (e.g. "the editor"). */
  label?: string
}
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[Quil] render error caught by ErrorBoundary', error, info)
    this.props.onError?.(error, info)
  }

  componentDidUpdate(prev: Props) {
    // Recover when the reset keys change (e.g. navigating to a different chapter).
    if (this.state.error && !shallowEqual(prev.resetKeys, this.props.resetKeys)) {
      this.setState({ error: null })
    }
  }

  reset = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(error, this.reset)
    return (
      <div className={styles.wrap} role="alert">
        <div className={styles.card}>
          <div className={styles.title}>Something went wrong{this.props.label ? ` in ${this.props.label}` : ''}.</div>
          <div className={styles.body}>
            Your saved work is safe in OneDrive and on this device. Reloading usually fixes it.
          </div>
          <div className={styles.actions}>
            <button type="button" onClick={() => window.location.reload()}>Reload</button>
            <button type="button" onClick={this.reset}>Try again</button>
          </div>
        </div>
      </div>
    )
  }
}

function shallowEqual(a?: unknown[], b?: unknown[]): boolean {
  if (a === b) return true
  if (!a || !b || a.length !== b.length) return false
  return a.every((v, i) => v === b[i])
}
